import {
  forwardRef,
  FocusEvent,
  useRef,
  KeyboardEvent,
  useImperativeHandle,
  useEffect,
  ChangeEvent,
  SyntheticEvent,
} from 'react';
import cn from 'classnames';

import styles from './Textarea.module.scss';

export interface ITextareaProps {
  className?: string;
  inputClassName?: string;
  id?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  label?: string;
  size?: 'small' | 'medium';
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  autoHeight?: boolean;
  autoFocus?: boolean;
  clearable?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  withWarning?: boolean;
  error?: string;
  warning?: string;
  withEditor?: boolean;
  isActiveFillInProcessMessage?: boolean;
  triggerCalculateHeight?: boolean;
  onInput?: (e: SyntheticEvent) => void;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onClear?: () => void;
}

export const Textarea = forwardRef(
  (
    {
      className = '',
      inputClassName = '',
      value,
      label,
      size,
      resize = 'horizontal',
      autoHeight = true,
      clearable,
      invalid,
      disabled,
      error,
      warning,
      id,
      withWarning,
      onClear,
      ...rest
    }: ITextareaProps,
    ref,
  ) => {
    const classNames = cn([
      styles.Textarea,
      styles[`Resize${resize.charAt(0).toUpperCase()}${resize.slice(1)}`],
      className,
      {
        [styles.TextareaInvalid]: Boolean(error),
        [styles.TextareaDisabled]: disabled,
        [styles.TextareaSizeSmall]: size === 'small',
        [styles.TextareaSizeMedium]: size === 'medium',
        [styles.TextareaAutoHeight]: Boolean(autoHeight),
      },
    ]);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => inputRef.current);
    const inputRefCurrent = inputRef.current;

    useEffect(() => {
      if (autoHeight && inputRefCurrent) {
        const { style } = inputRefCurrent;
        style.height = '';
        style.height = `${inputRefCurrent.scrollHeight / 16 + 2 / 16}rem`;
      }
    }, [autoHeight, inputRefCurrent, value]);

    const inputClassNames = cn(styles.Input, inputClassName, {
      [styles.WithWarning]: withWarning,
    });

    return (
      <div className={classNames}>
        {label && (
          <label className={styles.Label} htmlFor={id}>
            {label}
          </label>
        )}
        <textarea ref={inputRef} className={inputClassNames} value={value} disabled={disabled} {...rest} />
        {clearable && <button className={styles.ClearButton} type="button" onClick={onClear} hidden />}
        {withWarning && <span className={styles.WarningIcon} />}
        {error && <div className={styles.TextareaError}>{error}</div>}
        {warning && <div className={styles.Warning}>{warning}</div>}
      </div>
    );
  },
);
